import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import pallet from '../../../layouts/pallet';
import { StudentRollNoTableColumns } from '../utils/StudentRollNoTableColumn';
import { RollNoData } from '../types/rollNoDataType';
import { updateStudentRollNoApi } from '../../../apis/rollNoUpdate';
import { SnackbarSeverityType } from '../../../enums';
import { dataGridSx } from '../../common/styled/DataGrid';
import { ButtonWrapper, CancelButton, StyledButton } from '../styled';
import { downloadExcelFromJson } from '../../../utils/fileGeneration';
import { isEmpty } from 'lodash';
import tableColumnUtils from '../../../utils/tableColumnUtils';
import { PageWrapper } from '../../common/styled';
import CustomDataGridToolbar from '../../common/components/CustomDataGridToolbar';

const RollNumberUpdateTable = ({
  rollNoData,
  isSubmitting,
  showToast,
  onRollNoUpdateSuccess,
  handleSearch
}: {
  rollNoData: RollNoData[];
  isSubmitting: boolean;
  setRollNoData: React.Dispatch<React.SetStateAction<RollNoData[]>>;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  onRollNoUpdateSuccess: () => void;
  handleSearch: ({ displayToast }: { displayToast: boolean }) => Promise<void>;
}) => {
  const [editedRollNoData, setEditedRollNoData] =
    useState<RollNoData[]>(rollNoData);
  const [bulkRollNoUpdate, setBulkRollNoUpdate] = useState<
    Partial<RollNoData>[]
  >([]);

  const onSubmit = async () => {
    if (rollNoData.length === 0) return;
    try {
      const response = await updateStudentRollNoApi(bulkRollNoUpdate);
      const updateResponse = response as any;
      if (!isEmpty(updateResponse.unSuccessfulRecords)) {
        const headers = ['email'];
        const body = updateResponse.unSuccessfulRecords.map((r: any) => [
          r.email
        ]);
        downloadExcelFromJson(
          'Unsuccessful_Roll_No_Update',
          'UnsuccessfulRollNoUpdate',
          { headers, body }
        );
        await handleSearch({ displayToast: false });
      }
      showToast(
        'Successfully updated student roll no',
        SnackbarSeverityType.success
      );
      setBulkRollNoUpdate([]);
      onRollNoUpdateSuccess();
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error updating roll no',
        SnackbarSeverityType.error
      );
    }
  };

  const onRollNoChange = (id: string, rollNo: string) => {
    setEditedRollNoData((data) => {
      const updatedRollNoData = data.map((row) =>
        row.id === id ? { ...row, rollNo } : row
      );
      const allChangedRollNoData = updatedRollNoData.filter(
        (row, i) => row.rollNo !== rollNoData[i]?.rollNo
      );
      setBulkRollNoUpdate(
        allChangedRollNoData.map(({ email, rollNo }) => ({ email, rollNo }))
      );
      return updatedRollNoData;
    });
  };

  const onCancel = () => {
    setEditedRollNoData(rollNoData);
    setBulkRollNoUpdate([]);
  };

  const StudentRollNoColumns = StudentRollNoTableColumns(onRollNoChange);
  return (
    <PageWrapper>
      {bulkRollNoUpdate.length > 0 && (
        <ButtonWrapper>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>

          <StyledButton sx={{ width: 'min-content' }} onClick={onSubmit}>
            Update
          </StyledButton>
        </ButtonWrapper>
      )}

      <DataGrid
        rowHeight={50}
        rows={editedRollNoData}
        columns={StudentRollNoColumns}
        loading={isSubmitting}
        disableRowSelectionOnClick
        pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
        initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
        showToolbar
        slots={{
          toolbar: CustomDataGridToolbar
        }}
        sx={{
          ...dataGridSx(pallet),
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto'
          }
        }}
      />
    </PageWrapper>
  );
};

export default RollNumberUpdateTable;
